// src/hooks/useBroadcast.js
// ------------------------------------------------------------
// Live "rep" location broadcast.
//  - EVERY user subscribes to active broadcasters and sees them on the map.
//  - A REP (isRep) can toggle broadcasting: while live, their location +
//    display name are pushed to /broadcasters/{uid} and refreshed as they move.
// Rep-ness is gated client-side by the BENDER_REP login credential; Firestore
// rules only let a user write their OWN broadcaster doc.
// ------------------------------------------------------------
import { useState, useEffect, useRef } from 'react';
import {
  doc, setDoc, collection, query, where, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { resilientSnapshot } from '../utils/resilientSnapshot';

const STALE_MS = 10 * 60 * 1000; // hide reps not refreshed in 10 min (closed app)
const PUSH_MS  = 15000;          // how often a live rep re-pushes their location

const useBroadcast = (db, user, APP_ID, location, isRep) => {
  const [broadcasters, setBroadcasters] = useState([]);
  const [isBroadcasting, setIsBroadcasting] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('bender_broadcasting') === '1')
  );
  const [broadcastName, setBroadcastName] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('bender_broadcast_name')) || ''
  );

  // Latest location/name in refs so the push interval doesn't restart every GPS tick.
  const locationRef = useRef(location);
  const nameRef = useRef(broadcastName);
  const liveRef = useRef(false); // did we go live this session? (avoids junk "off" writes)
  useEffect(() => { locationRef.current = location; }, [location]);
  useEffect(() => { nameRef.current = broadcastName; }, [broadcastName]);

  // Persist toggle + name across reloads.
  useEffect(() => {
    localStorage.setItem('bender_broadcasting', isBroadcasting ? '1' : '0');
  }, [isBroadcasting]);
  useEffect(() => {
    localStorage.setItem('bender_broadcast_name', broadcastName);
  }, [broadcastName]);

  // --- SUBSCRIBE: all active broadcasters (visible to every user) ---
  useEffect(() => {
    // Gate on `user`: attaching before sign-in completes is a guaranteed
    // permission-denied on every cold start (rules require auth).
    if (!db || !APP_ID || !user) return;
    const q = query(
      collection(db, 'artifacts', APP_ID, 'public', 'data', 'broadcasters'),
      where('active', '==', true)
    );
    // resilientSnapshot: this attaches on mount, racing anonymous sign-in —
    // the first attempt gets permission-denied on every cold start and a dead
    // listener would mean no rep pins all session.
    const unsub = resilientSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) => Array.isArray(b.location))
          .filter((b) => !b.updatedAt || (now - b.updatedAt.toMillis()) < STALE_MS);
        setBroadcasters(list);
      },
      { label: 'broadcasters' }
    );
    return () => unsub();
  }, [db, APP_ID, user]);

  // --- PUSH / STOP: only reps write their own broadcaster doc ---
  useEffect(() => {
    if (!isRep || !user || !db) return;

    const write = async (active) => {
      const loc = locationRef.current;
      if (active && !loc) return; // nothing to broadcast until we have a fix
      try {
        await setDoc(
          doc(db, 'artifacts', APP_ID, 'public', 'data', 'broadcasters', user.uid),
          {
            displayName: (nameRef.current || 'Bender Rep').trim(),
            location: loc || null,
            active,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.error('Broadcast write error', e);
      }
    };

    if (isBroadcasting) {
      liveRef.current = true;
      write(true);
      const interval = setInterval(() => write(true), PUSH_MS);
      return () => clearInterval(interval);
    }

    // Toggled off — mark inactive, but only if we actually went live this session
    // (keeps brand-new reps from creating a junk doc).
    if (liveRef.current) {
      liveRef.current = false;
      write(false);
    }
  }, [isRep, isBroadcasting, user, db, APP_ID]);

  // --- KEEP-ALIVE: hold a Screen Wake Lock while broadcasting ---
  // Mobile browsers suspend timers + geolocation when the screen sleeps or the
  // tab is backgrounded, which would freeze a pocketed rep's location. A screen
  // wake lock keeps the page foreground-active so GPS + the push interval keep
  // running. (Web still can't broadcast with the screen fully OFF or another app
  // in front — that needs a native wrapper.) Re-acquired on return to visible,
  // since the OS releases the lock whenever the page is hidden.
  useEffect(() => {
    if (!isRep || !isBroadcasting) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let lock = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        if (document.visibilityState === 'visible') {
          lock = await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        // Denied (e.g. page not visible / battery saver) — heartbeat still tries.
      }
    };
    const onVisibility = () => {
      if (!cancelled && document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (lock) lock.release().catch(() => {});
    };
  }, [isRep, isBroadcasting]);

  // Immediately go off-air and mark my doc inactive. Used by logout: without this
  // a rep who signs out would linger on everyone's map until the stale window
  // (STALE_MS) expired. Must be awaited BEFORE auth.signOut(), since the write
  // needs the signed-in user.
  const stopBroadcast = async () => {
    setIsBroadcasting(false);
    if (!user || !db || !liveRef.current) return;
    liveRef.current = false;
    try {
      await setDoc(
        doc(db, 'artifacts', APP_ID, 'public', 'data', 'broadcasters', user.uid),
        { active: false, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error('Broadcast stop error', e);
    }
  };

  return {
    broadcasters, isBroadcasting, setIsBroadcasting,
    broadcastName, setBroadcastName, stopBroadcast,
  };
};

export default useBroadcast;
